/** Чистая логика правок РД «Инженер+» и документов изменения (№8 плана ПТО,
 *  выпуск 4): права по `access`, разбор `raw.engineer_edit` позиции, тексты «было
 *  → стало», бейдж и пометка строки, проверки форм, записи журнала.
 *
 *  Подписи видов, статусов, действий и причин — зеркало
 *  `osnova_pto::volume_edits` (Rust): экран и выгрузка журнала говорят одними
 *  словами.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`rdEdits/logic.test.mjs`), а Node не знает путей-алиасов. */
import type {
  PtoEditValues,
  PtoRdChangeDocMeta,
  PtoRdChangePositionBody,
  PtoRdEditsAccess,
  PtoRdEditsReason,
  PtoRenumberResult,
  PtoSheetRef,
  PtoTextCheck,
  PtoUnrecognized,
  PtoVolumeEdit,
  PtoVolumeEditLogEntry,
  PtoVolumeEditLogPage,
  PtoVolumePosition,
} from '@shared/api';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

/** Непустая строка без пробелов по краям; иначе null. */
function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Обрезать до `max` знаков с «…» (как `short` в Rust). */
export function short(s: string, max: number): string {
  const t = s.trim();
  const chars = Array.from(t);
  return chars.length <= max ? t : `${chars.slice(0, max).join('').trimEnd()}…`;
}

// ── Подписи (зеркало osnova_pto::volume_edits) ──

const ROLE_LABELS: Record<string, string> = {
  engineer_plus: 'Инженер+',
  engineer: 'Инженер',
  manager: 'Руководитель',
  admin: 'Администратор',
  director: 'Генеральный директор',
  pto_engineer: 'Инженер ПТО',
};

/** Подпись роли: «Инженер+», «Руководитель»…; неизвестная — код. */
export function roleLabel(code: string): string {
  return ROLE_LABELS[code] ?? code;
}

/** Роли через запятую. */
export function rolesText(codes: readonly string[]): string {
  return codes.map(roleLabel).join(', ');
}

const KIND_LABELS: Record<string, string> = {
  fix: 'исправление',
  split: 'разделение строки',
  per_unit: 'кратность «на 1 …»',
  manual: 'строка внесена вручную',
  change_doc: 'по документу изменения',
};

/** Вид правки. */
export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'ждёт подтверждения',
  active: 'действует',
  conflict: 'конфликт после переобработки',
  row_not_found: 'строка не найдена при переобработке',
  confirmed_by_program: 'подтверждено программой',
  closed_by_program: 'распознано программой — закрыто',
  reverted: 'отменено',
  declined: 'не подтверждено',
  withdrawn: 'отозвано',
  confirmed_by_revision: 'появилось в ревизии — закрыто',
  not_in_revision: 'нет в новой ревизии',
  needs_decision: 'спорно при новой ревизии',
};

/** Статус правки. */
export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

const ACTION_LABELS: Record<string, string> = {
  fix: 'Исправлено',
  split: 'Строка разделена',
  per_unit: 'Кратность «на 1 …»',
  missing_row: 'Строка не распознана — внесена',
  missing_row_pending: 'Строка внесена — ждёт подтверждения',
  missing_row_confirm: 'Строка подтверждена',
  missing_row_decline: 'Строка не подтверждена',
  revert: 'Правка отменена',
  resolve: 'Решение по правке',
  dispute: 'Спорно',
  decision: 'Решение по строке',
  reapplied: 'Правка применена снова (переобработка)',
  conflict: 'Конфликт при переобработке',
  row_not_found: 'Строка не найдена при переобработке',
  confirmed_by_program: 'Подтверждено программой',
  closed_by_program: 'Ручная строка закрыта — распознана программой',
  change_doc_create: 'Документ изменения загружен',
  change_doc_version: 'Новая версия файла документа',
  change_doc_withdraw: 'Документ изменения отозван',
  change_add: 'Внесено по документу изменения',
  change_withdraw: 'Позиция по документу отозвана',
  confirmed_by_revision: 'Появилась в новой ревизии — закрыта',
  not_in_revision: 'Нет в новой ревизии',
  needs_decision: 'Спорно при новой ревизии',
  reminder: 'Напоминание: ревизия не выдана',
  renumber: 'Строки перенумерованы вручную',
};

/** Действие журнала. */
export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

/** Действия журнала для фильтра — в порядке подписи. */
export function journalActions(): { code: string; label: string }[] {
  return Object.entries(ACTION_LABELS)
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

/** Признаки нераспознанной строки (`Unrecognized::label`). */
export const ISSUE_LABELS: Record<PtoUnrecognized, string> = {
  needs_review: 'на проверку',
  no_quantity: 'нет количества',
  no_unit: 'нет единицы',
  merged: 'склеенная строка',
  not_quantity: 'число — не количество',
  per_unit: '«на 1 …» без кратности',
  cells_disagree: 'расходится с текстом ячеек',
  vision_disagrees: 'расходится со вторым прочтением',
};

/** Причины, которых нет в справочниках `access`: их ставит сама программа. */
const SYSTEM_REASONS: Record<string, string> = {
  row_missed: 'Строка пропущена программой',
  rows_merged: 'Строки склеены',
  per_unit_count: 'Кратность «на 1 …» по явному числу РД',
};

/** Подпись причины по коду: причины исправления и «Спорно» из `access`, затем
 *  программные; неизвестная — код. */
export function reasonLabel(
  code: string | null | undefined,
  access?: Pick<PtoRdEditsAccess, 'fix_reasons' | 'dispute_reasons'> | null,
): string {
  if (!code) return '';
  const all: PtoRdEditsReason[] = [
    ...(access?.fix_reasons ?? []),
    ...(access?.dispute_reasons ?? []),
  ];
  return all.find((r) => r.code === code)?.label ?? SYSTEM_REASONS[code] ?? code;
}

// ── Права ──

/** Что можно пользователю с правками №8 в проекте. */
export interface RdEditRights {
  /** Правки включены в проекте (флаг `OSNOVA_PTO_RD_EDITS_ENABLED`). */
  enabled: boolean;
  /** «Исправить», документы изменения, подтверждение строк, «Перенумеровать» —
   *  роль не ниже «Инженер+» и запись в проект. Роль «Инженер» правок не видит. */
  canEdit: boolean;
  /** «Спорно» с листом и причиной (в журнал) — запись в проект. */
  canDispute: boolean;
  /** Режим флага: `off` | `test` | `on` (до ответа сервера — `off`). */
  mode: string;
}

/** Права по ответу `GET /projects/{id}/rd-edits/access` и праву записи в ПТО:
 *  нет ответа (старый сервер, загрузка) — правок нет, экран как прежде. */
export function rdEditRights(
  access: Pick<PtoRdEditsAccess, 'enabled' | 'can_edit' | 'mode'> | null | undefined,
  canWrite: boolean,
): RdEditRights {
  const enabled = !!access?.enabled;
  return {
    enabled,
    canEdit: enabled && !!access?.can_edit && canWrite,
    canDispute: enabled && canWrite,
    mode: access?.mode ?? 'off',
  };
}

// ── Позиция: правка и вид строки ──

/** Статусы, при которых правка «живёт» на позиции. */
export const LIVE_STATUSES = ['active', 'conflict', 'not_in_revision', 'needs_decision'];
/** Статусы закрытой позиции: решения по ней не меняются, она в истории. */
export const CLOSED_STATUSES = [
  'reverted',
  'withdrawn',
  'confirmed_by_revision',
  'closed_by_program',
  'declined',
];

/** Реквизиты документа изменения у позиции (Б). */
export interface ChangeDocRef {
  id: string | null;
  kind: string | null;
  title: string | null;
  number: string | null;
  date: string | null;
  approvalName: string | null;
}

/** Строка-кандидат новой ревизии для спорной позиции (Б). */
export interface RevisionCandidate {
  positionId: string;
  name: string;
  unit: string | null;
  quantity: number | null;
  label: string | null;
  sourceRef: string | null;
}

/** `raw.engineer_edit` позиции — правка, которая действует (или действовала) на
 *  строке. */
export interface EngineerEdit {
  editId: string | null;
  kind: string;
  status: string;
  byName: string | null;
  /** Роли автора на момент правки (`by_roles`); у прежних записей — нет. */
  byRoles: string[];
  at: string | null;
  reasonCode: string | null;
  sheetRef: Obj | null;
  /** Прочтение программы до правки (у ручной строки и позиции (Б) — нет). */
  machine: PtoEditValues | null;
  /** Текст графы «Основание» ведомости. */
  basis: string | null;
  changeDoc: ChangeDocRef | null;
  urgency: string | null;
  /** Ревизия сверки позиции (Б): «нет в ревизии N». */
  revisionNo: number | null;
  /** Прочтение программы при конфликте после переобработки. */
  conflictReading: PtoEditValues | null;
  confirmedByName: string | null;
  withdrawnReason: string | null;
  candidates: RevisionCandidate[];
}

/** Значения правки из JSON (`machine`, `value`, прочтение при конфликте). */
export function editValuesOf(v: unknown): PtoEditValues | null {
  const o = asObj(v);
  if (!o || typeof o.name !== 'string') return null;
  return {
    name: o.name,
    unit: str(o.unit),
    quantity: num(o.quantity),
    code: str(o.code),
    designation: str(o.designation),
    unit_mass: num(o.unit_mass),
  };
}

function changeDocRef(v: unknown): ChangeDocRef | null {
  const o = asObj(v);
  if (!o) return null;
  return {
    id: str(o.id),
    kind: str(o.kind),
    title: str(o.title),
    number: str(o.number),
    date: str(o.date) ?? str(o.doc_date),
    approvalName: str(asObj(o.approval)?.name),
  };
}

/** Правка позиции из `raw.engineer_edit`; нет — null. */
export function engineerEdit(raw: unknown): EngineerEdit | null {
  const e = asObj(asObj(raw)?.engineer_edit);
  if (!e) return null;
  const candidates = Array.isArray(e.candidates) ? e.candidates : [];
  return {
    editId: str(e.edit_id),
    kind: str(e.kind) ?? 'fix',
    status: str(e.status) ?? 'active',
    byName: str(e.by_name),
    byRoles: Array.isArray(e.by_roles)
      ? e.by_roles.filter((r): r is string => typeof r === 'string')
      : [],
    at: str(e.at),
    reasonCode: str(e.reason_code),
    sheetRef: asObj(e.sheet_ref),
    machine: editValuesOf(e.machine),
    basis: str(e.basis),
    changeDoc: changeDocRef(e.change_doc) ?? changeDocRef(asObj(raw)?.change_doc),
    urgency: str(e.urgency),
    revisionNo: num(e.revision_no),
    conflictReading: editValuesOf(asObj(e.conflict)?.reading),
    confirmedByName: str(e.confirmed_by_name),
    withdrawnReason: str(e.withdrawn_reason),
    candidates: candidates
      .map((c) => asObj(c))
      .filter((c): c is Obj => !!c && typeof c.position_id === 'string')
      .map((c) => ({
        positionId: c.position_id as string,
        name: typeof c.name === 'string' ? c.name : '',
        unit: str(c.unit),
        quantity: num(c.quantity),
        label: str(c.label),
        sourceRef: str(c.source_ref),
      })),
  };
}

/** Строка, которую программа не распознала, — внесена инженером. */
export function isManualRow(p: Pick<PtoVolumePosition, 'raw'>): boolean {
  return asObj(p.raw)?.source === 'manual';
}

/** Позиция по документу изменения (Б): «ДИ №k». */
export function isChangeDocRow(p: Pick<PtoVolumePosition, 'source_role' | 'doc_part'>): boolean {
  return p.source_role === 'rd_change' || p.doc_part?.trim() === 'ДИ';
}

/** Строка таблицы (спецификация, ВОР, геометрия) — их правит «Исправить». */
export function isTableRow(p: Pick<PtoVolumePosition, 'source_role'>): boolean {
  return p.source_role === 'rd_table' || p.source_role === 'rd_geometry';
}

/** Правка действует на позиции (её решения — только с правом правки). */
export function isLiveEdit(e: EngineerEdit | null): boolean {
  return !!e && LIVE_STATUSES.includes(e.status);
}

/** Позиция закрыта правкой: отменена, отозвана, закрыта ревизией или
 *  программой — решения по ней не меняются. */
export function isClosedRow(p: Pick<PtoVolumePosition, 'raw'>): boolean {
  const e = engineerEdit(p.raw);
  return !!e && CLOSED_STATUSES.includes(e.status);
}

/** Значения строки, которые меняет правка. */
export function valuesOf(
  p: Pick<PtoVolumePosition, 'name' | 'unit' | 'quantity' | 'code' | 'designation' | 'unit_mass'>,
): PtoEditValues {
  return {
    name: p.name,
    unit: p.unit ?? null,
    quantity: p.quantity ?? null,
    code: p.code ?? null,
    designation: p.designation ?? null,
    unit_mass: p.unit_mass ?? null,
  };
}

const NUMBER_RE = /\d+(?:[.,]\d+)?/g;

/** Числа текста: как написаны и без пробелов-разделителей разрядов («1 445» —
 *  и 1, 445, и 1445), как `numbers_in` в Rust. */
export function numbersIn(text: string): number[] {
  const joined = text.replace(/[ \u00a0\u202f]/g, '');
  const out: number[] = [];
  for (const t of [text, joined]) {
    for (const m of t.match(NUMBER_RE) ?? []) {
      const v = Number(m.replace(',', '.'));
      if (Number.isFinite(v)) out.push(v);
    }
  }
  return out;
}

function sameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) <= 1e-6 * Math.max(Math.abs(a), Math.abs(b), 1);
}

/**
 * Признаки нераспознанной строки на стороне экрана — для кнопки «Исправить…» в
 * строке (зеркало `unrecognized()` без «число — не количество»: его решает
 * сервер). Окно правки берёт признаки из `edit-options.issues` — они главные.
 * Позиции ИИ, ручные строки и «ДИ» не исправляются — пусто.
 */
export function looksUnrecognized(
  p: Pick<PtoVolumePosition, 'source_role' | 'raw' | 'needs_review' | 'quantity' | 'unit'>,
): PtoUnrecognized[] {
  if (!isTableRow(p) || isManualRow(p)) return [];
  const raw = asObj(p.raw) ?? {};
  const out: PtoUnrecognized[] = [];
  if (p.needs_review) out.push('needs_review');
  if (p.quantity === null || p.quantity === undefined) out.push('no_quantity');
  if (!p.unit || !p.unit.trim()) out.push('no_unit');
  const review = Array.isArray(raw.review) ? raw.review : [];
  if (review.some((c) => c === 'merged_row' || c === 'qty_from_merged_column')) out.push('merged');
  const set = (k: string) => raw[k] !== undefined && raw[k] !== null;
  if (set('per_unit') && !set('multiplier')) out.push('per_unit');
  const q = num(asObj(raw.multiplier)?.unit_qty) ?? p.quantity ?? null;
  const cells = Array.isArray(raw.cells)
    ? raw.cells.filter((c): c is string => typeof c === 'string')
    : [];
  if (
    q !== null &&
    cells.some((c) => c.trim()) &&
    !cells.some((c) => numbersIn(c).some((v) => sameNumber(v, q)))
  ) {
    out.push('cells_disagree');
  }
  if (asObj(raw.vision)?.verdict === 'differs') out.push('vision_disagrees');
  return out;
}

/** Что пользователь может сделать с решением строки («Принять», «Откл.»,
 *  «Спорно»): закрытые позиции не принимают решений; при включённых правках
 *  «Откл.» и его снятие на строке с количеством, решения по строке с правкой,
 *  ручной строке и позиции (Б) — только «Инженер+» (решение 9). */
export function decisionAllowed(
  p: Pick<PtoVolumePosition, 'raw' | 'quantity' | 'decision' | 'source_role' | 'doc_part'>,
  decision: 'accepted' | 'rejected' | 'disputed',
  rights: RdEditRights,
  canWrite: boolean,
): { allowed: boolean; reason: string | null } {
  if (!canWrite) return { allowed: false, reason: 'Нет права записи в проект.' };
  if (isClosedRow(p)) {
    return {
      allowed: false,
      reason: 'Позиция закрыта — решение не меняется, она остаётся в истории.',
    };
  }
  if (!rights.enabled || rights.canEdit) return { allowed: true, reason: null };
  const guarded = isLiveEdit(engineerEdit(p.raw)) || isManualRow(p) || isChangeDocRow(p);
  if (guarded) {
    return {
      allowed: false,
      reason:
        'Решение по строке с правкой, ручной строке и позиции по документу — только «Инженер+».',
    };
  }
  const rejectToggle =
    p.quantity !== null &&
    p.quantity !== undefined &&
    (decision === 'rejected' || p.decision === 'rejected') &&
    p.decision !== decision;
  if (rejectToggle) {
    return {
      allowed: false,
      reason:
        '«Откл.» и его снятие на строке с количеством — только «Инженер+»; отметьте «Спорно».',
    };
  }
  return { allowed: true, reason: null };
}

// ── Числа и «было → стало» ──

/** Число по-русски: разряды через неразрывный пробел, дробная часть через
 *  запятую, до 6 знаков без хвостовых нулей («1 445», «11,6»). */
export function fmtNumber(q: number): string {
  const r = Math.round(q * 1e6) / 1e6;
  const neg = r < 0;
  const [int, frac = ''] = Math.abs(r).toFixed(6).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  const tail = frac.replace(/0+$/, '');
  return `${neg ? '-' : ''}${grouped}${tail ? `,${tail}` : ''}`;
}

/** Количество с единицей: «1 445 м3»; нет количества — «—». */
export function qtyText(q: number | null | undefined, unit?: string | null): string {
  if (q === null || q === undefined) return '—';
  return `${fmtNumber(q)}${unit?.trim() ? ` ${unit.trim()}` : ''}`;
}

/** Значение строки кратко: «Грунт скальный» 1 445 м3. */
export function valuesText(v: PtoEditValues | null | undefined): string {
  if (!v) return '—';
  return `«${short(v.name, 60)}» ${qtyText(v.quantity, v.unit)}`;
}

function sameQty(a: number | null | undefined, b: number | null | undefined): boolean {
  const na = a ?? null;
  const nb = b ?? null;
  if (na === null || nb === null) return na === nb;
  return sameNumber(na, nb);
}

function opt(v: string | null | undefined): string {
  return v?.trim() || '—';
}

function optEq(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a?.trim() || null) === (b?.trim() || null);
}

/** Те же значения (количество и масса — с допуском), как `EditValues::same_as`. */
export function sameValues(a: PtoEditValues, b: PtoEditValues): boolean {
  return (
    a.name.trim() === b.name.trim() &&
    optEq(a.unit, b.unit) &&
    sameQty(a.quantity, b.quantity) &&
    optEq(a.code, b.code) &&
    optEq(a.designation, b.designation) &&
    sameQty(a.unit_mass, b.unit_mass)
  );
}

/** Количество или единица изменились — у позиции будет новый `record_uid`, а
 *  при привязке КСП правка запрещена. */
export function qtyOrUnitChanged(a: PtoEditValues, b: PtoEditValues): boolean {
  return !sameQty(a.quantity, b.quantity) || (a.unit?.trim() || '') !== (b.unit?.trim() || '');
}

/** Что изменено: «кол-во — → 1 445; ед. — → м3» (как `changes` в Rust).
 *  Ничего — пустая строка. */
export function changesText(before: PtoEditValues, after: PtoEditValues): string {
  const out: string[] = [];
  if (before.name.trim() !== after.name.trim()) {
    out.push(`наименование «${short(before.name, 40)}» → «${short(after.name, 40)}»`);
  }
  if (!sameQty(before.quantity, after.quantity)) {
    out.push(`кол-во ${qtyText(before.quantity)} → ${qtyText(after.quantity)}`);
  }
  if (!optEq(before.unit, after.unit)) out.push(`ед. ${opt(before.unit)} → ${opt(after.unit)}`);
  if (!optEq(before.code, after.code)) out.push(`шифр ${opt(before.code)} → ${opt(after.code)}`);
  if (!optEq(before.designation, after.designation)) {
    out.push(`обозначение ${opt(before.designation)} → ${opt(after.designation)}`);
  }
  if (!sameQty(before.unit_mass, after.unit_mass)) {
    out.push(`масса ед. ${qtyText(before.unit_mass)} → ${qtyText(after.unit_mass)}`);
  }
  return out.join('; ');
}

/** Лист и строка РД текстом: «СП01 л. 2 (стр. PDF 56), строка «поз. 5»» (как
 *  `SheetRef::text`); место в документе изменения — «стр. 3, п. 2.1». */
export function sheetRefText(ref: unknown): string {
  const o = asObj(ref);
  if (!o) return '';
  // Лист РД: страница PDF — число (у места в документе изменения — строка).
  const page = num(o.page);
  if (page !== null && page > 0) {
    const sheet = str(o.sheet);
    const row = str(o.row);
    let s = `стр. PDF ${page}`;
    if (sheet) s = `${sheet} (${s})`;
    if (row) s += `, строка «${short(row, 60)}»`;
    return s;
  }
  // Место в документе изменения: {page, item, …}.
  const parts: string[] = [];
  for (const [k, v] of Object.entries(o)) {
    const t = typeof v === 'number' ? String(v) : str(v);
    if (!t) continue;
    parts.push(k === 'page' ? `стр. ${t}` : k === 'item' ? `п. ${t}` : t);
  }
  return parts.join(', ');
}

/** Дата ISO → «21.10.2026» (дата без времени — как есть, без сдвига пояса). */
export function dateRu(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Момент ISO → «21.10.2026 14:05» (местное время). */
export function dateTimeRu(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dateRu(iso)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── Пометка строки ──

/** Документ изменения позиции (Б): «по документу ЗНИ № 12 от 01.10.2026 (до
 *  выдачи ревизии)». Не позиция (Б) — null. */
export function changeDocNote(
  p: Pick<PtoVolumePosition, 'raw' | 'source_role' | 'doc_part'>,
  kinds?: readonly PtoRdEditsReason[],
): string | null {
  if (!isChangeDocRow(p)) return null;
  const e = engineerEdit(p.raw);
  const doc = e?.changeDoc ?? changeDocRef(asObj(p.raw)?.change_doc);
  if (!doc) return 'по документу изменения (до выдачи ревизии)';
  const kind = doc.kind ? (kinds?.find((k) => k.code === doc.kind)?.label ?? doc.kind) : null;
  const what = [
    kind,
    doc.number ? `№ ${doc.number}` : null,
    doc.date ? `от ${dateRu(doc.date)}` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return `по документу ${what || 'изменения'} (до выдачи ревизии)`;
}

/** Бейдж правки у строки. */
export interface EditBadge {
  label: string;
  tone: 'ok' | 'warn' | 'muted';
}

/** Бейдж правки: «исправлено», «вручную», «ДИ», «конфликт», «отозвано»…; нет
 *  правки — null. */
export function editBadge(
  p: Pick<PtoVolumePosition, 'raw' | 'source_role' | 'doc_part'>,
): EditBadge | null {
  const e = engineerEdit(p.raw);
  if (!e) return isChangeDocRow(p) ? { label: 'ДИ', tone: 'ok' } : null;
  switch (e.status) {
    case 'conflict':
      return { label: 'конфликт правки', tone: 'warn' };
    case 'not_in_revision':
      return { label: `нет в ревизии${e.revisionNo ? ` ${e.revisionNo}` : ''}`, tone: 'warn' };
    case 'needs_decision':
      return { label: 'спорно при ревизии', tone: 'warn' };
    case 'withdrawn':
      return { label: 'отозвано', tone: 'muted' };
    case 'reverted':
      return { label: 'отменено', tone: 'muted' };
    case 'declined':
      return { label: 'не подтверждено', tone: 'muted' };
    case 'confirmed_by_revision':
      return { label: 'есть в ревизии', tone: 'muted' };
    case 'closed_by_program':
      return { label: 'распознано программой', tone: 'muted' };
    default:
      break;
  }
  switch (e.kind) {
    case 'manual':
      return { label: 'вручную', tone: 'ok' };
    case 'change_doc':
      return { label: 'ДИ', tone: 'ok' };
    case 'per_unit':
      return { label: 'кратность', tone: 'ok' };
    case 'split':
      return { label: 'разделена', tone: 'ok' };
    default:
      return { label: 'исправлено', tone: 'ok' };
  }
}

/** Кто и когда: «Иванов И. И., 21.10.2026». */
function whoWhen(e: EngineerEdit): string {
  const who = e.byName
    ? `${e.byName}${e.byRoles.length > 0 ? ` (${rolesText(e.byRoles)})` : ''}`
    : null;
  return [who, dateRu(e.at)].filter(Boolean).join(', ');
}

/** Пометка под наименованием: «было → стало», лист, причина, кто (ФИО, роль) и
 *  когда; у ручной строки — откуда она; у позиции (Б) — документ; у закрытых —
 *  почему. Нет правки — null. `access` — справочники причин и видов
 *  документов. */
export function editNote(
  p: Pick<
    PtoVolumePosition,
    | 'raw'
    | 'source_role'
    | 'doc_part'
    | 'name'
    | 'unit'
    | 'quantity'
    | 'code'
    | 'designation'
    | 'unit_mass'
  >,
  access?: Pick<PtoRdEditsAccess, 'fix_reasons' | 'dispute_reasons' | 'change_doc_kinds'> | null,
): string | null {
  const e = engineerEdit(p.raw);
  const docNote = changeDocNote(p, access?.change_doc_kinds);
  if (!e) return docNote;
  const by = whoWhen(e);
  const tail = by ? ` · ${by}` : '';
  switch (e.status) {
    case 'conflict':
      return `Конфликт после переобработки: программа прочла строку иначе — ${valuesText(
        e.conflictReading ?? valuesOf(p),
      )}; правка не применена, строка на проверку. Решите: применить правку снова или принять прочтение программы.`;
    case 'not_in_revision':
      return `${docNote ?? 'по документу изменения'} — нет в ревизии${
        e.revisionNo ? ` ${e.revisionNo}` : ''
      }: вне сумм, решает «Инженер+»`;
    case 'needs_decision':
      return `${docNote ?? 'по документу изменения'} — спорно при ревизии${
        e.revisionNo ? ` ${e.revisionNo}` : ''
      }: решает инженер, в суммах — ревизия`;
    case 'withdrawn':
      return `Отозвано${e.withdrawnReason ? `: ${e.withdrawnReason}` : ''} — вне сумм, в истории`;
    case 'reverted':
      return 'Правка отменена — строка вне сумм, в истории';
    case 'confirmed_by_revision':
      return `Материал есть в ревизии${e.revisionNo ? ` ${e.revisionNo}` : ''} — позиция закрыта без двойного счёта`;
    case 'closed_by_program':
      return 'Программа распознала ту же строку — ручная строка закрыта без двойного счёта';
    default:
      break;
  }
  const sheet = sheetRefText(e.sheetRef);
  if (e.kind === 'change_doc') return `${docNote ?? 'по документу изменения'}${tail}`;
  if (e.kind === 'manual') {
    const confirmed = e.confirmedByName ? `; подтвердил ${e.confirmedByName}` : '';
    return `Строка не распознана программой — внесена вручную${sheet ? ` по ${sheet}` : ''}${tail}${confirmed}`;
  }
  const now = valuesOf(p);
  const diff = e.machine ? changesText(e.machine, now) : '';
  const what =
    e.kind === 'per_unit'
      ? 'Кратность «на 1 …»'
      : e.kind === 'split'
        ? 'Строка разделена'
        : 'Исправлено';
  // Причина — у «Исправить» (у разделения и кратности она в самом действии).
  const reason = e.kind === 'fix' && e.reasonCode ? reasonLabel(e.reasonCode, access) : '';
  return `${what}${sheet ? ` по ${sheet}` : ''}${diff ? `: ${diff}` : ''}${
    reason ? `; причина — ${reason}` : ''
  }${tail}`;
}

// ── Формы ──

/** Разбор числа из поля: «1 445,5» → 1445.5; пусто — null; не число —
 *  undefined. */
export function parseNumber(text: string | null | undefined): number | null | undefined {
  const t = (text ?? '').replace(/[ \u00a0\u202f]/g, '').replace(',', '.');
  if (!t) return null;
  if (!/^-?\d+(\.\d+)?$/.test(t)) return undefined;
  const v = Number(t);
  return Number.isFinite(v) ? v : undefined;
}

/** Поля значения строки в форме (строками, как ввёл пользователь). */
export interface ValueForm {
  name: string;
  unit: string;
  quantity: string;
  code: string;
  designation: string;
  unitMass: string;
}

/** Поля формы по значению строки. */
export function valueForm(v: PtoEditValues | null | undefined): ValueForm {
  return {
    name: v?.name ?? '',
    unit: v?.unit ?? '',
    quantity:
      v?.quantity === null || v?.quantity === undefined ? '' : String(v.quantity).replace('.', ','),
    code: v?.code ?? '',
    designation: v?.designation ?? '',
    unitMass:
      v?.unit_mass === null || v?.unit_mass === undefined
        ? ''
        : String(v.unit_mass).replace('.', ','),
  };
}

/** Значение для API; ошибки разбора — в `errors`. */
export function formValues(f: ValueForm): { value: PtoEditValues; errors: string[] } {
  const errors: string[] = [];
  const qty = parseNumber(f.quantity);
  const mass = parseNumber(f.unitMass);
  if (!f.name.trim()) errors.push('Укажите наименование строки.');
  if (qty === undefined || (typeof qty === 'number' && qty < 0)) {
    errors.push('Количество — неотрицательное число (например, 1 445,5).');
  }
  if (mass === undefined || (typeof mass === 'number' && mass < 0)) {
    errors.push('Масса единицы — неотрицательное число.');
  }
  return {
    value: {
      name: f.name.trim(),
      unit: f.unit.trim() || null,
      quantity: typeof qty === 'number' && qty >= 0 ? qty : null,
      code: f.code.trim() || null,
      designation: f.designation.trim() || null,
      unit_mass: typeof mass === 'number' && mass >= 0 ? mass : null,
    },
    errors,
  };
}

/** Лист и строка РД в форме. */
export interface SheetForm {
  page: string;
  sheet: string;
  row: string;
  note: string;
}

/** Ссылка на лист для API; ошибки — в `errors`. `rowRequired` — строка на листе
 *  обязательна («Исправить», кратность). */
export function sheetRefOf(
  f: SheetForm,
  totalPages: number | null | undefined,
  rowRequired: boolean,
): { sheetRef: PtoSheetRef; errors: string[] } {
  const errors: string[] = [];
  const page = Number(f.page.trim());
  const max = totalPages && totalPages > 0 ? totalPages : null;
  if (!f.page.trim() || !Number.isInteger(page) || page < 1 || (max !== null && page > max)) {
    errors.push(`Укажите лист РД: страница PDF от 1 до ${max ?? 'последней'}.`);
  }
  if (rowRequired && !f.row.trim() && !f.note.trim()) {
    errors.push('Укажите строку на листе РД (позицию или текст строки).');
  }
  return {
    sheetRef: {
      page: Number.isInteger(page) ? page : 0,
      sheet: f.sheet.trim() || null,
      row: f.row.trim() || null,
      note: f.note.trim() || null,
    },
    errors,
  };
}

/** Поля листа по позиции: страница PDF, лист по основной надписи («СП01 л. 2»),
 *  строка — «поз. N» из `source_ref`. */
export function sheetFormOf(
  p: Pick<PtoVolumePosition, 'page_number' | 'raw' | 'source_ref'>,
): SheetForm {
  const sheet = asObj(asObj(p.raw)?.sheet);
  const code = str(sheet?.code);
  const no = typeof sheet?.no === 'number' ? String(sheet.no) : str(sheet?.no);
  const pos = /поз\. *(\d+[\d.]*)/.exec(p.source_ref ?? '');
  return {
    page: p.page_number > 0 ? String(p.page_number) : '',
    sheet: code && no ? `${code} л. ${no}` : '',
    row: pos ? `поз. ${pos[1]}` : '',
    note: '',
  };
}

/** Причина из справочника; «другое» — с комментарием. */
function reasonErrors(
  code: string,
  list: readonly PtoRdEditsReason[],
  other: string,
  comment: string,
): string[] {
  if (!code) return ['Выберите причину из списка.'];
  if (list.length > 0 && !list.some((r) => r.code === code)) return ['Причина не из списка.'];
  if (code === other && !comment.trim()) return ['Для причины «другое» нужен комментарий.'];
  return [];
}

/** Проверки «Исправить» (как на сервере): значение, лист и строка, причина;
 *  значение должно измениться; при привязке КСП количество и единицу менять
 *  нельзя. Пусто — можно отправлять. */
export function fixErrors(args: {
  value: ValueForm;
  sheet: SheetForm;
  reasonCode: string;
  comment: string;
  reasons: readonly PtoRdEditsReason[];
  current: PtoEditValues;
  /** Прочтение программы, если у строки уже есть правка «Исправить». */
  machine: PtoEditValues | null;
  totalPages: number | null;
  kspLocked: boolean;
}): string[] {
  const { value, errors } = formValues(args.value);
  errors.push(...sheetRefOf(args.sheet, args.totalPages, true).errors);
  errors.push(...reasonErrors(args.reasonCode, args.reasons, 'other_recognition', args.comment));
  if (errors.length === 0) {
    if (sameValues(value, args.current)) errors.push('Значение не изменилось.');
    else if (args.machine && sameValues(value, args.machine)) {
      errors.push('Значение совпадает с прочтением программы — отмените правку («Отменить»).');
    } else if (args.kspLocked && qtyOrUnitChanged(value, args.current)) {
      errors.push(
        'Позиция привязана к графику КСП: количество и единицу исправить нельзя — только наименование и обозначение.',
      );
    }
  }
  return errors;
}

/** Проверки «Разделить строку»: от 2 до 50 частей, у каждой наименование. */
export function splitErrors(
  parts: readonly ValueForm[],
  sheet: SheetForm,
  totalPages: number | null,
): string[] {
  const errors: string[] = [];
  if (parts.length < 2 || parts.length > 50) errors.push('Частей разделённой строки — от 2 до 50.');
  parts.forEach((p, i) => {
    for (const e of formValues(p).errors)
      errors.push(`Часть ${i + 1}: ${e.charAt(0).toLowerCase()}${e.slice(1)}`);
  });
  errors.push(...sheetRefOf(sheet, totalPages, false).errors);
  return errors;
}

/** Проверки кратности «на 1 …»: число элементов > 0 (до 1 000 000), лист и
 *  строка, где оно написано. */
export function perUnitErrors(
  count: string,
  sheet: SheetForm,
  totalPages: number | null,
): string[] {
  const errors: string[] = [];
  const n = parseNumber(count);
  if (typeof n !== 'number' || n <= 0 || n > 1_000_000) {
    errors.push('Число элементов — положительное число не больше 1 000 000.');
  }
  errors.push(...sheetRefOf(sheet, totalPages, true).errors);
  return errors;
}

/** Проверки «Спорно»: лист и причина из списка; «другое» — с комментарием. */
export function disputeErrors(args: {
  sheet: SheetForm;
  reasonCode: string;
  comment: string;
  reasons: readonly PtoRdEditsReason[];
  totalPages: number | null;
}): string[] {
  return [
    ...sheetRefOf(args.sheet, args.totalPages, false).errors,
    ...reasonErrors(args.reasonCode, args.reasons, 'other', args.comment),
  ];
}

/** Итог проверки строки по тексту листа — для окна «Строка не распознана». */
export function textCheckNote(check: PtoTextCheck | null | undefined): string {
  if (!check) return '';
  if (check.found) return 'Строка найдена в тексте листа — внесена в объёмы.';
  const why = !check.sheet_has_text
    ? 'у листа нет текстового слоя (скан)'
    : [
        check.name_found
          ? null
          : `в тексте листа найдено слов наименования: ${check.words_found} из ${check.words_total}`,
        check.quantity_found ? null : 'количества в тексте листа нет',
      ]
        .filter(Boolean)
        .join('; ');
  return `Строка не найдена в тексте листа (${why}) — ждёт подтверждения другим пользователем с правом правки, не автором.`;
}

// ── Документ изменения ──

/** Расширения файла документа изменения (как на сервере). */
export const CHANGE_DOC_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'tif',
  'tiff',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'odt',
  'ods',
];

/** Поля загрузки документа изменения. */
export interface ChangeDocForm {
  kind: string;
  title: string;
  number: string;
  docDate: string;
  documentIds: string[];
  approvalName: string;
  approvalPosition: string;
  approvalDate: string;
  /** Инженер подтверждает: отметка согласования Заказчика есть в файле. */
  approvalConfirmed: boolean;
  urgency: string;
  sqRequestId: string;
  comment: string;
  file: { name: string; size: number } | null;
}

export function emptyChangeDocForm(): ChangeDocForm {
  return {
    kind: '',
    title: '',
    number: '',
    docDate: '',
    documentIds: [],
    approvalName: '',
    approvalPosition: '',
    approvalDate: '',
    approvalConfirmed: false,
    urgency: '',
    sqRequestId: '',
    comment: '',
    file: null,
  };
}

/** Сегодня — `ГГГГ-ММ-ДД` (местная дата). */
export function todayIso(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Ошибки файла документа изменения: есть, расширение из списка, размер. */
export function changeDocFileErrors(
  file: { name: string; size: number } | null,
  maxBytes: number,
): string[] {
  if (!file) return ['Приложите файл документа изменения.'];
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  if (!CHANGE_DOC_EXTENSIONS.includes(ext)) {
    return [`Файл документа изменения: ${CHANGE_DOC_EXTENSIONS.join(', ')}.`];
  }
  if (file.size <= 0) return ['Файл пуст.'];
  if (maxBytes > 0 && file.size > maxBytes) {
    return [`Файл больше ${fileSizeText(maxBytes)} — загрузите сжатую копию.`];
  }
  return [];
}

/** Проверки загрузки документа изменения (§3.2 канона): вид из списка, название
 *  как в документе, номер и дата, комплекты, согласование Заказчика (ФИО,
 *  должность, дата, отметка в файле), обоснование крайней необходимости, файл.
 *  Без согласования и обоснования документ не отправляется. */
export function changeDocErrors(
  f: ChangeDocForm,
  opts: { kinds: readonly PtoRdEditsReason[]; maxBytes: number; today: string },
): string[] {
  const errors: string[] = [];
  if (!f.kind || (opts.kinds.length > 0 && !opts.kinds.some((k) => k.code === f.kind))) {
    errors.push('Выберите вид документа из списка.');
  }
  if (!f.title.trim()) errors.push('Укажите название — как в документе.');
  if (!f.number.trim()) errors.push('Укажите номер документа.');
  if (!f.docDate) errors.push('Укажите дату документа.');
  else if (f.docDate > opts.today) errors.push('Дата документа — не позже сегодняшней.');
  if (f.documentIds.length === 0)
    errors.push('Отметьте комплекты РД, к которым относится документ.');
  if (!f.approvalName.trim()) errors.push('Согласование Заказчика: укажите ФИО согласовавшего.');
  if (!f.approvalPosition.trim()) errors.push('Согласование Заказчика: укажите должность.');
  if (!f.approvalDate) errors.push('Согласование Заказчика: укажите дату согласования.');
  else if (f.approvalDate > opts.today) errors.push('Дата согласования — не позже сегодняшней.');
  if (!f.approvalConfirmed) {
    errors.push('Подтвердите, что отметка согласования Заказчика есть в файле документа.');
  }
  if (!f.urgency.trim()) {
    errors.push('Обоснуйте крайнюю необходимость: почему нельзя ждать выдачи ревизии РД.');
  }
  errors.push(...changeDocFileErrors(f.file, opts.maxBytes));
  return errors;
}

/** Реквизиты для поля `meta` формы загрузки. */
export function changeDocMeta(f: ChangeDocForm): PtoRdChangeDocMeta {
  return {
    kind: f.kind,
    title: f.title.trim(),
    number: f.number.trim(),
    doc_date: f.docDate,
    document_ids: [...f.documentIds],
    approval: {
      name: f.approvalName.trim(),
      position: f.approvalPosition.trim(),
      date: f.approvalDate,
      confirmed: f.approvalConfirmed,
    },
    urgency: f.urgency.trim(),
    sq_request_id: f.sqRequestId || null,
    comment: f.comment.trim() || null,
  };
}

/** Поля позиции (Б) — материала по документу изменения. */
export interface ChangePositionForm {
  documentId: string;
  value: ValueForm;
  docPage: string;
  docItem: string;
  urgency: string;
  comment: string;
}

/** Проверки позиции (Б): комплект документа, наименование, единица и
 *  количество больше нуля, обоснование крайней необходимости. */
export function changePositionErrors(f: ChangePositionForm): string[] {
  const errors: string[] = [];
  if (!f.documentId) errors.push('Выберите комплект РД из указанных в документе.');
  const { value, errors: valueErrors } = formValues(f.value);
  errors.push(...valueErrors);
  if (!value.unit) errors.push('Укажите единицу измерения.');
  if (valueErrors.length === 0 && !(typeof value.quantity === 'number' && value.quantity > 0)) {
    errors.push('Количество — больше нуля.');
  }
  if (!f.urgency.trim()) {
    errors.push('Позиция не вносится без обоснования крайней необходимости.');
  }
  return errors;
}

/** Тело внесения позиции (Б). */
export function changePositionBody(f: ChangePositionForm): PtoRdChangePositionBody {
  const docRef: { page?: string; item?: string } = {};
  if (f.docPage.trim()) docRef.page = f.docPage.trim();
  if (f.docItem.trim()) docRef.item = f.docItem.trim();
  return {
    document_id: f.documentId,
    value: formValues(f.value).value,
    doc_ref: Object.keys(docRef).length > 0 ? docRef : null,
    urgency: f.urgency.trim(),
    comment: f.comment.trim() || null,
  };
}

/** Размер файла: «350 КБ», «1,2 МБ». */
export function fileSizeText(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${fmtNumber(Math.round((bytes / (1024 * 1024)) * 10) / 10)} МБ`;
}

// ── Журнал ──

/** «Было» / «стало» записи журнала текстом (как `describe` в Rust). */
export function describeValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  const values = editValuesOf(v);
  if (values && values.name) {
    let s = valuesText(values);
    if (values.designation) s += `; обозн. ${values.designation}`;
    return s;
  }
  const o = asObj(v);
  if (o && Array.isArray(o.parts)) {
    return o.parts.map((p) => describeValue(asObj(p)?.value)).join(' | ');
  }
  if (o && Array.isArray(o.numbers)) {
    const shown = o.numbers.slice(0, 100).map((n) => (typeof n === 'string' ? n : 'без номера'));
    return `${shown.join(', ')}${o.numbers.length > 100 ? ` … (всего ${o.numbers.length})` : ''}`;
  }
  if (o && 'decision' in o) {
    switch (o.decision) {
      case 'accepted':
        return 'принято';
      case 'rejected':
        return 'откл.';
      case 'disputed':
        return 'спорно';
      default:
        return 'без решения';
    }
  }
  if (o && typeof o.status === 'string') return statusLabel(o.status);
  return JSON.stringify(v);
}

/** Пары «было → стало» записи «Перенумеровать» (`before.numbers[i]` →
 *  `after.numbers[i]`, `null` — номер снят). */
export function renumberPairs(
  e: Pick<PtoVolumeEditLogEntry, 'action' | 'before' | 'after'>,
): [string, string | null][] {
  if (e.action !== 'renumber') return [];
  const from = asObj(e.before)?.numbers;
  const to = asObj(e.after)?.numbers;
  if (!Array.isArray(from)) return [];
  return from.map((b, i) => [
    typeof b === 'string' ? b : '—',
    Array.isArray(to) && typeof to[i] === 'string' ? (to[i] as string) : null,
  ]);
}

/** Реквизиты документа изменения записи журнала (как `describe_doc` в Rust). */
export function changeDocSnapshotText(v: unknown, kinds?: readonly PtoRdEditsReason[]): string {
  const o = asObj(v);
  if (!o) return '';
  const kind = str(o.kind);
  const kindText = kind ? (kinds?.find((k) => k.code === kind)?.label ?? kind) : 'Документ';
  const date = str(o.doc_date) ?? str(o.date);
  let s = `${kindText} № ${str(o.number) ?? '—'}${date ? ` от ${dateRu(date)}` : ''}`;
  const title = str(o.title);
  if (title) s += ` «${short(title, 60)}»`;
  const approval = str(asObj(o.approval)?.name);
  if (approval) s += `; согласовал ${approval}`;
  return s;
}

/** Запись журнала для показа. */
export interface LogEntryView {
  id: number;
  when: string;
  /** ФИО автора; у записи системы — «система». */
  who: string;
  roles: string;
  action: string;
  /** Комплект, ревизия и строка: «…-КЖ1 · ревизия 2 · ВР01 №5». */
  place: string;
  before: string;
  after: string;
  sheet: string;
  reason: string;
  comment: string;
  doc: string;
  /** Пары номеров «Перенумеровать». */
  pairs: [string, string | null][];
}

/** Запись журнала: кто (ФИО, роль), когда, что, «было → стало», лист, причина. */
export function logEntryView(
  e: PtoVolumeEditLogEntry,
  access?: Pick<PtoRdEditsAccess, 'fix_reasons' | 'dispute_reasons' | 'change_doc_kinds'> | null,
): LogEntryView {
  const system = e.actor_kind === 'system';
  return {
    id: e.id,
    when: dateTimeRu(e.at),
    who: system ? 'система' : e.actor_name?.trim() || 'пользователь',
    roles: system ? '' : rolesText(e.actor_roles ?? []),
    action: actionLabel(e.action),
    place: [
      e.document_code,
      e.revision_no !== null && e.revision_no !== undefined ? `ревизия ${e.revision_no}` : null,
      e.position_label,
    ]
      .filter(Boolean)
      .join(' · '),
    before: e.action === 'renumber' ? '' : describeValue(e.before),
    after: e.action === 'renumber' ? '' : describeValue(e.after),
    sheet: sheetRefText(e.sheet_ref),
    reason: reasonLabel(e.reason_code, access),
    comment: e.comment?.trim() ?? '',
    doc: changeDocSnapshotText(e.change_doc, access?.change_doc_kinds),
    pairs: renumberPairs(e),
  };
}

/** Записи страниц журнала по порядку без повторов (при перезапросе страниц
 *  одна запись может прийти дважды). */
export function mergeLogPages(
  pages: readonly PtoVolumeEditLogPage[] | undefined,
): PtoVolumeEditLogEntry[] {
  const seen = new Set<number>();
  const out: PtoVolumeEditLogEntry[] = [];
  for (const page of pages ?? []) {
    for (const e of page.items) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}

/** Авторы записей журнала (для фильтра «Кто») — по ФИО, без повторов. */
export function journalActors(
  entries: readonly PtoVolumeEditLogEntry[],
): { id: string; name: string }[] {
  const by = new Map<string, string>();
  for (const e of entries) {
    if (e.actor_id && !by.has(e.actor_id)) by.set(e.actor_id, e.actor_name?.trim() || e.actor_id);
  }
  return [...by.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

// ── Правки ревизии и позиции (Б) при новой ревизии ──

/** Правка, которая ждёт решения человека: строка не найдена в тексте листа,
 *  конфликт или «строка не найдена» после переобработки. */
export const ATTENTION_STATUSES = ['pending', 'conflict', 'row_not_found'];

/** Можно ли пользователю подтвердить строку: право правки и не автор. */
export function canConfirmPending(
  e: Pick<PtoVolumeEdit, 'kind' | 'status' | 'created_by'>,
  me: string | null | undefined,
  rights: RdEditRights,
): { allowed: boolean; reason: string | null } {
  if (e.kind !== 'manual' || e.status !== 'pending') return { allowed: false, reason: null };
  if (!rights.canEdit)
    return { allowed: false, reason: 'Подтверждает пользователь с правом правки («Инженер+»).' };
  if (me && e.created_by === me) {
    return {
      allowed: false,
      reason: 'Строку подтверждает другой пользователь с правом правки, не автор.',
    };
  }
  return { allowed: true, reason: null };
}

/** Позиции (Б) прежних ревизий, ждущие решения при новой ревизии, — по
 *  комплектам (шифр по порядку). */
export function changeDocDecisionGroups(
  edits: readonly PtoVolumeEdit[],
): { documentId: string; code: string; items: PtoVolumeEdit[] }[] {
  const by = new Map<string, { documentId: string; code: string; items: PtoVolumeEdit[] }>();
  for (const e of edits) {
    if (e.kind !== 'change_doc' || !['not_in_revision', 'needs_decision'].includes(e.status))
      continue;
    const g = by.get(e.document_id);
    if (g) g.items.push(e);
    else
      by.set(e.document_id, { documentId: e.document_id, code: e.document_code ?? '', items: [e] });
  }
  return [...by.values()].sort((a, b) => a.code.localeCompare(b.code, 'ru'));
}

// ── «Перенумеровать» ──

/** Итог «Перенумеровать» одной фразой: сколько строк сменили номер, получили
 *  его впервые, остались без изменений; пронумеровано по частям. */
export function renumberSummary(
  r: Pick<
    PtoRenumberResult,
    'renumbered' | 'assigned' | 'cleared' | 'unchanged' | 'numbered' | 'parts'
  >,
): string {
  const parts = Object.entries(r.parts ?? {})
    .sort(([a], [b]) => a.localeCompare(b, 'ru'))
    .map(([part, n]) => `${part} — ${n}`)
    .join(', ');
  const tail = `Пронумеровано строк: ${r.numbered}${parts ? ` (${parts})` : ''}.`;
  if (r.renumbered === 0 && r.assigned === 0 && r.cleared === 0) {
    return `Номера не изменились. ${tail}`;
  }
  const bits = [`номер сменился у строк: ${r.renumbered}`];
  if (r.assigned > 0) bits.push(`получили номер впервые: ${r.assigned}`);
  if (r.cleared > 0) bits.push(`номер снят (нет части РД): ${r.cleared}`);
  bits.push(`без изменений: ${r.unchanged}`);
  return `Перенумеровано: ${bits.join(', ')}. ${tail}`;
}
